import React from 'react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import Seo from '../components/Seo';
import { Markdown } from './BlogPages';

const LegalShell = ({ title, updated, path, children }) => (
  <div className="min-h-screen bg-white text-slate-900">
    <Seo title={`${title} | Hipro Technologies`} description={`${title} for hiprotechnologies.com — Hipro Technologies.`} path={path} />
    <Navbar />
    <section className="pt-28 pb-8 bg-slate-950 text-white">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <span className="text-xs font-semibold uppercase tracking-widest text-orange-400">Legal</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold">{title}</h1>
        <p className="mt-3 text-sm text-slate-400">Last updated: {updated}</p>
      </div>
    </section>
    <article className="py-12">
      <div className="max-w-3xl mx-auto px-5 lg:px-8" data-testid="legal-page-body">{children}</div>
    </article>
    <Footer />
  </div>
);

const PRIVACY = `## 1. Who we are

Hipro Technologies ("we", "us") provides software development, AI automation and digital marketing services. Our corporate office is at 15A, 4th Floor, City Vista, Fountain Road, Kharadi, Pune, Maharashtra 411014, India, with additional offices in India, the USA and the UK. For any privacy question, contact **info@hiprotechnologies.com**.

## 2. Information we collect

- **Contact details** you submit through our consultation, booking, careers or newsletter forms (name, email, phone, company)
- **Job application data** including your CV/resume, work history and answers you provide when applying for a role
- **Account data** if you create an account (email, name, profile picture from Google sign-in, and a securely hashed password if you set one)
- **Usage data** such as pages visited and approximate location (derived from IP) used to show your nearest office and appropriate currency
- **Chat messages** you send to our website assistant

## 3. How we use your information

- To respond to enquiries, prepare estimates and schedule meetings you request
- To process job applications, including AI-assisted screening of applications against the role requirements
- To send transactional emails (booking confirmations, application status updates, account verification and password reset links)
- To send newsletter content, only if you subscribed — every email includes a way to opt out
- To improve our website and services

We do **not** sell your personal data to third parties.

## 4. Legal bases (GDPR)

Where GDPR applies, we process data on the basis of consent (newsletter, chat), contract (service delivery, job applications) and legitimate interest (site analytics, security).

## 5. Data sharing

We share data only with service providers needed to operate the site — email delivery, cloud hosting, calendar/meeting scheduling (Google) and AI processing providers — each bound by their own data-protection obligations. We may disclose data if required by law.

## 6. Data retention

Enquiries and leads are kept while relevant to our relationship. Job applications are kept for consideration for future roles unless you ask us to delete them. Account data is kept until you delete your account.

## 7. Your rights

You may request access, correction, deletion, or a copy of your personal data, and withdraw consent at any time by writing to **info@hiprotechnologies.com**. If you are in the EU/UK you may also lodge a complaint with your supervisory authority.

## 8. Security

Passwords are stored using strong one-way hashing, connections are encrypted with TLS, and access to personal data is restricted to authorised staff.

## 9. Changes

We may update this policy from time to time. Material changes will be reflected by the "Last updated" date on this page.`;

const TERMS = `## 1. Agreement

By accessing hiprotechnologies.com (the "Site") you agree to these Terms of Service. If you do not agree, please do not use the Site.

## 2. Services

Hipro Technologies provides software development, AI automation and digital marketing services. Information on the Site — including indicative pricing — is for general guidance and does not constitute a binding offer. Specific engagements are governed by a separate written agreement or statement of work.

## 3. Use of the Site

You agree not to:

- Misuse forms, chat or booking tools (spam, scraping, automated submissions)
- Attempt to gain unauthorised access to any part of the Site or its systems
- Submit content that is unlawful, misleading or infringes third-party rights

## 4. Accounts

You are responsible for keeping your account credentials confidential and for activity under your account. We may suspend accounts that violate these terms.

## 5. Job applications

Submitting an application does not create an employment relationship. You confirm the information you provide is accurate. We may use AI-assisted tools to help screen applications; hiring decisions are made by humans.

## 6. Intellectual property

All Site content — text, graphics, logos, case studies and code — is owned by or licensed to Hipro Technologies and may not be reproduced without permission. Client names and testimonials are used with consent.

## 7. Disclaimers

The Site is provided "as is". While we aim for accuracy, we make no warranties that content is error-free or uninterrupted. Case-study results describe specific client engagements and are not a guarantee of future outcomes.

## 8. Limitation of liability

To the maximum extent permitted by law, Hipro Technologies is not liable for indirect, incidental or consequential damages arising from use of the Site. Nothing limits liability that cannot be limited by law.

## 9. Governing law

These terms are governed by the laws of India, with courts of Pune, Maharashtra having jurisdiction, unless a separate service agreement states otherwise.

## 10. Contact

Questions about these terms: **info@hiprotechnologies.com**.`;

const COOKIES = `## 1. What are cookies?

Cookies and similar technologies (such as browser local storage) are small pieces of data stored on your device that help websites function and remember your preferences.

## 2. What we use

We keep it minimal:

- **Session cookie (essential)** — a secure, httpOnly \`session_token\` cookie that keeps you signed in to your account. Without it, sign-in does not work.
- **Language preference (essential)** — your chosen language (EN/IT/HI) is stored in browser local storage so the site loads in your language.
- **Currency/region lookup (functional)** — we perform a one-time IP-based lookup to suggest your currency and nearest office; no tracking identifier is stored.
- **Embedded maps (third party)** — office maps are embedded from Google Maps, which may set its own cookies when the map loads. See Google's cookie policy for details.

## 3. What we do NOT use

We currently do not use advertising cookies, cross-site trackers or third-party analytics identifiers on this Site.

## 4. Managing cookies

You can clear or block cookies in your browser settings at any time. Blocking the essential session cookie will prevent you from staying signed in, but the rest of the Site will work normally.

## 5. Contact

Questions about this policy: **info@hiprotechnologies.com**.`;

export function PrivacyPolicyPage() {
  return <LegalShell title="Privacy Policy" updated="June 2026" path="/privacy-policy"><Markdown content={PRIVACY} /></LegalShell>;
}

export function TermsPage() {
  return <LegalShell title="Terms of Service" updated="June 2026" path="/terms-of-service"><Markdown content={TERMS} /></LegalShell>;
}

export function CookiePolicyPage() {
  return <LegalShell title="Cookie Policy" updated="June 2026" path="/cookie-policy"><Markdown content={COOKIES} /></LegalShell>;
}
